import { useEffect, useState } from "react";
import { get, post } from "@/lib/api";
import { journeyById, canPerform, allowedRoleNames } from "@/data/canvas";
import { usePersona } from "@/context/PersonaContext";
import { JourneyShell } from "@/components/JourneyShell";
import { GovernedActionPanel } from "@/components/GovernedActionPanel";
import { LayerTag } from "@/components/LayerTag";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, Clock, Bell, FileText, Info } from "lucide-react";

const journey = journeyById("induction");

const STATUS_LABEL = {
  invited: { text: "Invited", cls: "bg-stone-100 text-stone-600" },
  in_progress: { text: "In progress", cls: "bg-[#FEF3C7] text-[#B45309]" },
  completed: { text: "Completed", cls: "bg-[#D1FAE5] text-[#065F46]" },
};

export default function InductionJourney() {
  const { persona } = usePersona();
  const [list, setList] = useState([]);
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const d = await get("/mill/inductions");
    setList(d.inductions);
    setSelId((cur) => cur || d.inductions[0]?.id);
  };
  useEffect(() => { load(); }, []);

  const sel = list.find((i) => i.id === selId);
  const canToggle = canPerform(persona.id, "induction.toggle");
  const canAck = canPerform(persona.id, "induction.acknowledge");
  const canRemind = canPerform(persona.id, "induction.remind");
  const allDone = sel ? sel.checklist.every((c) => c.done) : false;

  const toggle = async (item) => {
    if (!canToggle) return;
    setBusy(true);
    const updated = await post(`/mill/inductions/${sel.id}/toggle`, { item_id: item.id, done: !item.done });
    setList((l) => l.map((x) => (x.id === updated.id ? updated : x)));
    setBusy(false);
  };

  const acknowledge = async () => {
    setBusy(true);
    try {
      const updated = await post(`/mill/inductions/${sel.id}/acknowledge`, { actor_name: persona.name });
      setList((l) => l.map((x) => (x.id === updated.id ? updated : x)));
      toast.success("Policy acknowledged — induction complete.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not acknowledge.");
    }
    setBusy(false);
  };

  const remind = async () => {
    setBusy(true);
    const updated = await post(`/mill/inductions/${sel.id}/remind`, {});
    setList((l) => l.map((x) => (x.id === updated.id ? updated : x)));
    toast.success("Reminder sent (illustrative comms).");
    setBusy(false);
  };

  return (
    <JourneyShell journey={journey}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        {/* Volunteer list */}
        <div className="space-y-2" data-testid="induction-list">
          <p className="px-1 text-xs font-semibold uppercase tracking-[0.14em] text-mill-muted">Volunteers</p>
          {list.map((i) => {
            const st = STATUS_LABEL[i.status] || STATUS_LABEL.invited;
            return (
              <button
                key={i.id}
                data-testid={`induction-select-${i.id}`}
                onClick={() => setSelId(i.id)}
                className={`w-full rounded-xl border p-3 text-left transition-colors ${selId === i.id ? "border-mill-moss bg-white shadow-sm" : "border-stone-200 bg-white/60 hover:bg-white"}`}
              >
                <p className="font-semibold text-mill-ink">{i.volunteer_name}</p>
                <p className="text-xs text-mill-muted">{i.role}</p>
                <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${st.cls}`}>{st.text}</span>
              </button>
            );
          })}
        </div>

        {/* Detail */}
        {sel && (
          <div className="space-y-6" data-testid="induction-detail">
            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-xl font-semibold text-mill-ink">{sel.volunteer_name}</h2>
                  <p className="text-sm text-mill-muted">{sel.role} · invited by {sel.invited_by}</p>
                </div>
                <LayerTag layer="self_service" />
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-mill-alt px-4 py-3 text-sm">
                <FileText className="h-4 w-4 text-mill-moss" />
                <span className="text-mill-ink">Current policy pack: <strong>{sel.policy_version}</strong></span>
              </div>

              {/* Checklist */}
              <div className="mt-5">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-semibold text-mill-ink">Induction checklist</p>
                  <span className="text-xs text-mill-muted">{sel.checklist.filter((c) => c.done).length}/{sel.checklist.length} complete</span>
                </div>
                <div className="space-y-2">
                  {sel.checklist.map((item) => (
                    <label
                      key={item.id}
                      data-testid={`checklist-item-${item.id}`}
                      className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${item.done ? "border-[#A7F3D0] bg-[#F0FDF4]" : "border-stone-200 bg-white"} ${canToggle ? "cursor-pointer" : "cursor-not-allowed opacity-90"}`}
                    >
                      <Checkbox checked={item.done} disabled={!canToggle || busy} onCheckedChange={() => toggle(item)} data-testid={`checkbox-${item.id}`} />
                      <span className={`text-sm ${item.done ? "text-mill-ink" : "text-mill-muted"}`}>{item.label}</span>
                    </label>
                  ))}
                </div>
                {!canToggle && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-mill-muted" data-testid="checklist-guard">
                    <Info className="h-3.5 w-3.5" /> Only the {allowedRoleNames("induction.toggle")} completes their own checklist. Switch to Volunteer to tick items.
                  </p>
                )}
              </div>

              {/* Acknowledge / status */}
              <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-stone-200 pt-5">
                {sel.acknowledged ? (
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#D1FAE5] px-3 py-1.5 text-sm font-semibold text-[#065F46]" data-testid="induction-completed">
                    <CheckCircle2 className="h-4 w-4" /> Acknowledged & complete
                  </span>
                ) : (
                  <Button
                    data-testid="acknowledge-button"
                    disabled={!canAck || !allDone || busy}
                    onClick={acknowledge}
                    className="bg-mill-moss hover:bg-mill-moss-dark"
                  >
                    Acknowledge policy {sel.policy_version.split(" ")[0]}
                  </Button>
                )}
                <Button
                  variant="outline"
                  data-testid="remind-button"
                  disabled={!canRemind || sel.acknowledged || busy}
                  onClick={remind}
                >
                  <Bell className="mr-1.5 h-4 w-4" /> Send reminder
                </Button>
              </div>
              {!canAck && !sel.acknowledged && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-mill-muted"><Info className="h-3.5 w-3.5" /> Only the {allowedRoleNames("induction.acknowledge")} may acknowledge. Reminders are sent by the {allowedRoleNames("induction.remind")}.</p>
              )}

              {/* Reminders */}
              {sel.reminders?.length > 0 && (
                <div className="mt-4 space-y-1.5" data-testid="reminders-log">
                  {sel.reminders.map((r, i) => (
                    <p key={i} className="flex items-center gap-2 text-xs text-mill-muted"><Clock className="h-3.5 w-3.5" /> {r.by}: {r.note}</p>
                  ))}
                </div>
              )}
            </div>

            <GovernedActionPanel
              journey="induction"
              refId={sel.id}
              action="induction.sign"
              governed={journey.governed}
              disabledReason={!sel.acknowledged ? "This receipt becomes available once the induction is acknowledged and complete." : null}
            />
          </div>
        )}
      </div>
    </JourneyShell>
  );
}
